import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { CatalogFeature } from "@/types/catalog-feature";
import {
  CreateSubscriptionPlanPayload,
  PlanFeatureValuePayload,
} from "@/types/subscription-plan";
import { useFeatures } from "../hooks/useFeatures";
import { useCreateCustomPlan } from "../hooks/useSubscriptionPlanMutations";
import { useSubscriptionTenantOptions } from "../hooks/useSubscriptionTenantOptions";
import { getVisibleCustomPlanFeatureOptions } from "../utils/customPlanFeatureVisibility";
import { PlanFeaturesBadgesField } from "./PlanFeaturesBadgesField";

type CreateCustomPlanDialogProps = {
  open: boolean;
  onClose: () => void;
};

const EMPTY_FEATURES: CatalogFeature[] = [];

function buildFeaturePayload(
  ids: string[],
  featureOptions: CatalogFeature[],
  featureValueById: Readonly<Partial<Record<string, string>>>,
): PlanFeatureValuePayload[] {
  const featureById = new Map(
    featureOptions.map((feature) => [feature.id, feature]),
  );

  return ids.map((id) => {
    const feature = featureById.get(id);
    const rawValue =
      feature?.type === "NUMBER"
        ? (featureValueById[id] ?? String(feature.limit ?? "")).trim()
        : (featureValueById[id] ?? "true");

    return {
      featureId: id,
      value: rawValue,
    };
  });
}

export const CreateCustomPlanDialog: React.FC<CreateCustomPlanDialogProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { data: featuresData, isLoading: featuresLoading } = useFeatures(open);
  const features = featuresData ?? EMPTY_FEATURES;
  const { data: tenantsResponse, isLoading: tenantsLoading } =
    useSubscriptionTenantOptions(open);
  const { mutateAsync, isPending } = useCreateCustomPlan();

  const [tenantId, setTenantId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [currency, setCurrency] = useState("usd");
  const [enableCoreFeatures, setEnableCoreFeatures] = useState(false);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
  const [featureValueById, setFeatureValueById] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (open) {
      setTenantId("");
      setTitle("");
      setDescription("");
      setPriceInput("");
      setCurrency("usd");
      setEnableCoreFeatures(false);
      setSelectedFeatureIds([]);
      setFeatureValueById({});
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTenant = tenantId.trim();
    const trimmedTitle = title.trim();
    if (trimmedTenant === "") {
      toast.error(t("subscriptions.plans.custom.create.validation.tenantId"));
      return;
    }
    if (trimmedTitle === "") {
      toast.error(t("subscriptions.plans.custom.create.validation.title"));
      return;
    }
    const price = Number.parseFloat(priceInput.trim());
    if (!Number.isFinite(price) || price < 0) {
      toast.error(t("subscriptions.plans.custom.create.validation.price"));
      return;
    }

    const payload: CreateSubscriptionPlanPayload = {
      title: trimmedTitle,
      price,
      currency: currency.trim() === "" ? "usd" : currency.trim().toLowerCase(),
      enableCoreFeatures,
    };

    const descTrimmed = description.trim();
    if (descTrimmed !== "") {
      payload.description = descTrimmed;
    }
    const feats = buildFeaturePayload(
      selectedFeatureIds,
      featureOptions,
      featureValueById,
    );
    if (feats.length > 0) {
      payload.features = feats;
    }

    try {
      await mutateAsync({ tenantId: trimmedTenant, payload });
      toast.success(t("subscriptions.plans.custom.create.successToast"));
      onClose();
    } catch {
      toast.error(t("subscriptions.plans.custom.create.errorToast"));
    }
  };

  const featureOptions: CatalogFeature[] =
    getVisibleCustomPlanFeatureOptions(features);
  const tenants = tenantsResponse?.items ?? [];
  let tenantPlaceholder = t("subscriptions.plans.custom.create.selectTenant");

  if (tenantsLoading) {
    tenantPlaceholder = t("subscriptions.plans.custom.create.loadingTenants");
  } else if (tenants.length === 0) {
    tenantPlaceholder = t("subscriptions.plans.custom.create.noTenants");
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("subscriptions.plans.custom.create.title")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-foreground text-sm font-medium">
              {t("subscriptions.plans.custom.create.tenantId")}
            </span>
            <Select
              value={tenantId}
              onValueChange={setTenantId}
              disabled={tenantsLoading || tenants.length === 0}
            >
              <SelectTrigger>
                <SelectValue placeholder={tenantPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((tenant) => (
                  <SelectItem key={tenant.id} value={tenant.id}>
                    {`${tenant.name} (${tenant.slug})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Input
            label={t("subscriptions.plans.custom.create.planTitle")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoComplete="off"
          />
          <Textarea
            label={t("subscriptions.plans.custom.create.description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              inputMode="decimal"
              step="any"
              min={0}
              label={t("subscriptions.plans.custom.create.price")}
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
            />
            <Input
              label={t("subscriptions.plans.custom.create.currency")}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              maxLength={3}
            />
          </div>
          <div className="border-border flex items-center justify-between rounded-lg border px-3 py-2">
            <span className="text-foreground text-sm font-medium">
              {t("subscriptions.plans.featureField.includeCore")}
            </span>
            <Switch
              checked={enableCoreFeatures}
              onCheckedChange={setEnableCoreFeatures}
            />
          </div>
          <PlanFeaturesBadgesField
            featureOptions={featureOptions}
            selectedFeatureIds={selectedFeatureIds}
            onSelectedFeatureIdsChange={setSelectedFeatureIds}
            featuresLoading={featuresLoading}
            featureValueById={featureValueById}
            onFeatureValueByIdChange={setFeatureValueById}
            labels={{
              section: t("subscriptions.plans.custom.create.features"),
              addPlaceholder: t(
                "subscriptions.plans.custom.create.selectFeature",
              ),
              removeFeature: t(
                "subscriptions.plans.custom.create.removeFeature",
              ),
              enabled: t("subscriptions.plans.featureField.enabled"),
              value: t("subscriptions.plans.featureField.value"),
            }}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={onClose}
            >
              {t("subscriptions.plans.custom.create.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {t("subscriptions.plans.custom.create.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
